import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { WhatsappLogo, PaperPlaneTilt, Users, MagnifyingGlass, ChatCircleText, Broadcast, CheckCircle } from "@phosphor-icons/react";
import { openWhatsapp } from "../utils/waOpener";

const WA_GREEN = "#25D366";

const WhatsAppHub = ({ user, logout }) => {
  const [contacts, setContacts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, direct: 0, broadcast: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("direct"); // direct | broadcast
  const [broadcastTags, setBroadcastTags] = useState("");
  const [sending, setSending] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_name: user?.name || ""
      });
      const [cRes, lRes, sRes] = await Promise.all([
        api.get(`${API}/crm/contacts?${params.toString()}`),
        axios.get(`${API}/whatsapp/logs?sent_by=${user?.user_id || ""}`),
        axios.get(`${API}/whatsapp/stats?sent_by=${user?.user_id || ""}`),
      ]);
      setContacts(cRes.data);
      setLogs(lRes.data);
      setStats(sRes.data);
    } catch (err) { toast.error("Failed to load WhatsApp hub"); }
    finally { setLoading(false); }
  }, [user?.role, user?.name, user?.user_id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const withPhone = useMemo(
    () => contacts.filter(c => c.phone && c.phone.trim()),
    [contacts]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return withPhone;
    return withPhone.filter(c =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.company || "").toLowerCase().includes(q)
    );
  }, [withPhone, search]);

  const broadcastTargets = useMemo(() => {
    if (mode !== "broadcast") return [];
    const needed = broadcastTags.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
    if (needed.length === 0) return withPhone;
    return withPhone.filter(c => {
      const tags = (c.tags || []).map(t => String(t).toLowerCase());
      return needed.some(n => tags.includes(n));
    });
  }, [mode, broadcastTags, withPhone]);

  const sendDirect = async () => {
    if (!selected || !message.trim()) { toast.error("Pick a contact and write a message"); return; }
    const result = await openWhatsapp(selected.phone, message);
    await axios.post(`${API}/whatsapp/log`, {
      contact_id: selected.contact_id, contact_name: selected.name,
      phone: result.phone || selected.phone, message,
      sent_by: user?.user_id || "", sent_by_name: user?.name || "",
      kind: "direct"
    });
    toast.success("Opened WhatsApp — logged to CRM");
    setMessage("");
    fetchAll();
  };

  const sendBroadcast = async () => {
    if (!message.trim()) { toast.error("Write a message first"); return; }
    if (broadcastTargets.length === 0) { toast.error("No contacts match"); return; }
    if (!window.confirm(`Open ${broadcastTargets.length} WhatsApp tabs and log each send?`)) return;
    setSending(true);
    try {
      for (const c of broadcastTargets) {
        const personalized = message.replace(/\{\{name\}\}/g, c.name || "there");
        // eslint-disable-next-line no-await-in-loop
        const result = await openWhatsapp(c.phone, personalized);
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/whatsapp/log`, {
          contact_id: c.contact_id, contact_name: c.name,
          phone: result.phone || c.phone, message: personalized,
          sent_by: user?.user_id || "", sent_by_name: user?.name || "",
          kind: "broadcast"
        });
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, 400));
      }
      toast.success(`Broadcast queued for ${broadcastTargets.length} contacts`);
      setMessage("");
      fetchAll();
    } finally { setSending(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8 max-w-6xl">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold flex items-center gap-3" style={{fontFamily: 'Outfit, sans-serif'}}>
                <WhatsappLogo size={40} weight="fill" style={{ color: WA_GREEN }} />
                WhatsApp Hub
              </h1>
              <p className="text-base text-slate-700 mt-2">
                Chat directly with {user?.role === 'bde' ? 'your' : 'all'} customers. Sends open in your WhatsApp Business app.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatBox icon={ChatCircleText} value={stats.total} label="Total Messages" color="#002FA7" />
            <StatBox icon={PaperPlaneTilt} value={stats.direct} label="Direct Chats" color={WA_GREEN} />
            <StatBox icon={Broadcast} value={stats.broadcast} label="Broadcasts" color="#F59E0B" />
          </div>

          {/* Mode Switcher */}
          <div className="flex gap-1 mb-6 border-b border-slate-200">
            <TabButton active={mode === "direct"} onClick={() => setMode("direct")} testId="wa-mode-direct">
              <ChatCircleText size={16} weight={mode === "direct" ? "fill" : "regular"} /> 1-on-1 Chat
            </TabButton>
            <TabButton active={mode === "broadcast"} onClick={() => setMode("broadcast")} testId="wa-mode-broadcast">
              <Broadcast size={16} weight={mode === "broadcast" ? "fill" : "regular"} /> Broadcast
            </TabButton>
          </div>

          {mode === "direct" ? (
            <DirectChatView
              loading={loading}
              contacts={filtered}
              selected={selected}
              setSelected={setSelected}
              search={search} setSearch={setSearch}
              message={message} setMessage={setMessage}
              sendDirect={sendDirect}
              user={user}
              logs={logs}
            />
          ) : (
            <BroadcastView
              targets={broadcastTargets}
              broadcastTags={broadcastTags} setBroadcastTags={setBroadcastTags}
              message={message} setMessage={setMessage}
              sendBroadcast={sendBroadcast}
              sending={sending}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`px-5 py-3 text-sm font-medium transition-colors duration-300 border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap ${active ? 'border-[#25D366] text-[#128C7E]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
  >
    {children}
  </button>
);

const StatBox = ({ icon: IIcon, value, label, color }) => (
  <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
    <IIcon size={24} weight="duotone" style={{color}} className="mb-3" />
    <div className="text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color}}>{value}</div>
    <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{label}</div>
  </div>
);

const DirectChatView = ({ loading, contacts, selected, setSelected, search, setSearch, message, setMessage, sendDirect, user, logs }) => {
  const selectedLogs = selected ? logs.filter(l => l.contact_id === selected.contact_id) : [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-0 bg-white border border-slate-200 rounded-none shadow-sm" style={{ minHeight: 500 }}>
      {/* Left: contacts list */}
      <div className="md:col-span-1 border-r border-slate-200">
        <div className="p-4 border-b border-slate-200 relative">
          <MagnifyingGlass size={16} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="wa-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search contacts…"
            className="w-full rounded-none border border-slate-300 focus:border-[#25D366] p-2 pl-8 text-sm outline-none"
          />
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: 450 }}>
          {loading ? (
            <div className="flex justify-center py-12"><div className="loading-spinner"></div></div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Users size={32} weight="duotone" className="mx-auto text-slate-300 mb-2" />
              No contacts with phone numbers yet.
            </div>
          ) : contacts.map(c => (
            <button
              key={c.contact_id}
              data-testid={`wa-contact-${c.contact_id}`}
              onClick={() => setSelected(c)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors duration-300 ${selected?.contact_id === c.contact_id ? 'bg-green-50 border-l-4 border-l-[#25D366]' : ''}`}
            >
              <div className="font-semibold text-sm">{c.name}</div>
              <div className="text-xs text-slate-500">{c.company || c.phone}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: chat composer */}
      <div className="md:col-span-2 flex flex-col">
        {selected ? (
          <>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: WA_GREEN }}>
                {selected.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div>
                <div className="font-bold">{selected.name}</div>
                <div className="text-xs text-slate-500">{selected.phone} · {selected.email}</div>
              </div>
            </div>

            <div className="flex-1 p-6 overflow-y-auto bg-slate-50" style={{ maxHeight: 320 }}>
              {selectedLogs.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-12">
                  No previous WhatsApp messages with {selected.name}.<br/>
                  Your sent messages from here will be logged for audit.
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedLogs.slice().reverse().map(l => (
                    <div key={l.whatsapp_log_id} className="flex justify-end">
                      <div className="max-w-xs bg-[#DCF8C6] rounded-lg px-4 py-2 shadow-sm">
                        <div className="text-sm whitespace-pre-wrap">{l.message}</div>
                        <div className="text-[0.65rem] text-slate-500 mt-1 flex items-center gap-1">
                          <CheckCircle size={10} weight="fill" />
                          {new Date(l.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-white">
              <textarea
                data-testid="wa-message"
                rows={3}
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={`Type a message for ${selected.name}… Use {{name}} to personalize`}
                className="w-full rounded-none border border-slate-300 focus:border-[#25D366] p-3 outline-none text-sm"
              />
              <div className="flex justify-between items-center mt-3">
                <p className="text-xs text-slate-500">Opens your WhatsApp — you'll tap send on your phone.</p>
                <button
                  data-testid="wa-send-direct"
                  onClick={sendDirect}
                  disabled={!message.trim()}
                  className="rounded-none text-white font-bold px-6 py-3 flex items-center gap-2 transition-opacity duration-300 disabled:opacity-40"
                  style={{ backgroundColor: WA_GREEN }}
                >
                  <PaperPlaneTilt size={16} weight="fill" /> Send via WhatsApp
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-500 p-12">
            <WhatsappLogo size={64} weight="duotone" style={{ color: WA_GREEN }} className="mb-4" />
            <p className="text-lg font-semibold">Select a contact to start chatting</p>
            <p className="text-sm mt-1 text-slate-400">All messages are logged to CRM with timestamp + activity trail.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const BroadcastView = ({ targets, broadcastTags, setBroadcastTags, message, setMessage, sendBroadcast, sending }) => (
  <div className="bg-white border border-slate-200 rounded-none shadow-sm p-8 space-y-5">
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Filter by tags (comma-separated, leave blank for all)</label>
      <input
        data-testid="wa-broadcast-tags"
        type="text"
        value={broadcastTags}
        onChange={e => setBroadcastTags(e.target.value)}
        placeholder="e.g. vip, hot-lead, enterprise"
        className="w-full rounded-none border border-slate-300 focus:border-[#25D366] p-3 outline-none"
      />
      <div className="mt-2 text-sm">
        <span className="inline-flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 px-3 py-1">
          <Users size={14} weight="fill" /> {targets.length} contact{targets.length === 1 ? '' : 's'} will receive this
        </span>
      </div>
    </div>
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">
        Broadcast message — use <code className="bg-slate-100 px-1 text-xs">{"{{name}}"}</code> to personalize
      </label>
      <textarea
        data-testid="wa-broadcast-message"
        rows={5}
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Hi {{name}}, exciting offer this Diwali — 25% OFF on all CCTV systems. Use code DIWALI25…"
        className="w-full rounded-none border border-slate-300 focus:border-[#25D366] p-3 outline-none text-sm"
      />
    </div>

    {targets.length > 0 && (
      <div className="bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 max-h-32 overflow-y-auto">
        <div className="font-bold uppercase tracking-wider mb-1">Recipients:</div>
        {targets.map(t => (
          <span key={t.contact_id} className="inline-block mr-2 mb-1 px-2 py-0.5 bg-white border border-slate-200">
            {t.name}
          </span>
        ))}
      </div>
    )}

    <div className="flex justify-between items-center border-t border-slate-200 pt-5">
      <p className="text-xs text-slate-500 max-w-md">
        <strong>Note:</strong> This opens a WhatsApp tab per contact with the message pre-filled. You confirm send on your phone.
        For fully automated sending, integrate Twilio WhatsApp Business API later.
      </p>
      <button
        data-testid="wa-send-broadcast"
        onClick={sendBroadcast}
        disabled={sending || !message.trim() || targets.length === 0}
        className="rounded-none text-white font-bold px-8 py-3 flex items-center gap-2 transition-opacity duration-300 disabled:opacity-40"
        style={{ backgroundColor: WA_GREEN }}
      >
        <Broadcast size={16} weight="fill" /> {sending ? "Queuing…" : `Broadcast to ${targets.length}`}
      </button>
    </div>
  </div>
);

export default WhatsAppHub;
